/**
 * The shape of a pricing rule, as a form holds it and as the API takes it.
 *
 * Pure functions, apart from the component that renders them, for one reason:
 * the rules the server enforces are not all expressible in an `<input>`, and a
 * field that only fails server-side teaches the manager nothing. A 400 saying
 * "`startTime` and `endTime` must be set together, or neither" arrives after the
 * save, in English, naming a JSON key the manager never typed. {@link ruleProblems}
 * says the same thing next to the field before the request is made.
 *
 * Every bound below is a second copy of a `class-validator` decorator on
 * `CreateResourcePricingRuleDto`, and a second copy is a copy that can drift.
 * It is worth it here for the same reason `GUEST_SETTING_BOUNDS` is: the API
 * publishes its limits nowhere a browser can read, and the alternative is a box
 * that takes `4000` for a block length and answers with a toast that does not
 * say what number would have worked.
 */
import { PricingUnit, priceForDuration } from '@/lib/domain';
import type { PricingRuleRow } from '../pricingListsApi';

/**
 * `MAX_RULE_MINUTES` on the DTO — "a resource can only be priced in blocks a
 * shift can plausibly contain". Both minute fields are `@IsInt() @Min(1) @Max()`
 * against it.
 */
export const MAX_RULE_MINUTES = 24 * 60;

export interface Bound {
  min: number;
  max: number;
}

export const RULE_BOUNDS = {
  durationMinutes: { min: 1, max: MAX_RULE_MINUTES },
  minMinutes: { min: 1, max: MAX_RULE_MINUTES },
} as const satisfies Record<string, Bound>;

/**
 * `IsCurrencyAmount()` — `^\d+(\.\d{1,4})?$` against `MAX_MONEY_SCALE = 4`.
 *
 * Non-negative and no exponent on purpose: `-5`, `.5` and `1e2` are every bit as
 * refused as five decimal places, and a `type="number"` box will happily produce
 * all three.
 */
export const PRICE_PATTERN = /^\d+(\.\d{1,4})?$/;
export const MAX_PRICE_PLACES = 4;

/** `IsClockTime()` — `HH:mm`, 24-hour. */
export const CLOCK_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Sunday-first, matching `daysOfWeek`'s `0 = Sunday … 6 = Saturday`. */
export const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/**
 * The rule as the form holds it: every number is the text that was typed.
 *
 * Holding them as `number` would mean the box either rejects keystrokes or
 * rewrites them mid-edit — clearing a `60` to type `120` makes an empty box,
 * which is not `0`. Text in, parsed at the boundary, and a parse that fails is
 * what refuses the save.
 */
export interface RuleDraft {
  name: string;
  unit: PricingUnit;
  price: string;
  durationMinutes: string;
  minMinutes: string;
  daysOfWeek: number[];
  /** Both ends together or neither — see {@link ruleProblems}. */
  startTime: string;
  endTime: string;
  priority: string;
  isActive: boolean;
}

export function blankDraft(): RuleDraft {
  return {
    name: '',
    unit: PricingUnit.PER_HOUR,
    price: '',
    durationMinutes: '60',
    minMinutes: '',
    daysOfWeek: [],
    startTime: '',
    endTime: '',
    priority: '0',
    isActive: true,
  };
}

export function draftOf(rule: PricingRuleRow): RuleDraft {
  return {
    name: rule.name,
    unit: rule.unit,
    // The price is put back exactly as the API sent it, never re-rounded to the
    // currency's minor unit on the way in. `Decimal(14,4)` can hold `25.5005`
    // where a two-place venue would render `25.50`; prefilling the rendered
    // figure would make merely opening the dialog and pressing Save a silent
    // repricing of the rule.
    price: rule.price,
    durationMinutes: rule.durationMinutes === null ? '60' : String(rule.durationMinutes),
    minMinutes: rule.minMinutes === null ? '' : String(rule.minMinutes),
    daysOfWeek: [...rule.daysOfWeek],
    startTime: rule.startTime ?? '',
    endTime: rule.endTime ?? '',
    priority: String(rule.priority),
    isActive: rule.isActive,
  };
}

/** Which control an objection belongs beside. */
export type RuleField =
  | 'name'
  | 'price'
  | 'durationMinutes'
  | 'minMinutes'
  | 'window'
  | 'priority';

export interface RuleProblem {
  field: RuleField;
  /** An `spaces.pricingRules.errors.*` leaf. */
  key: string;
  params?: Record<string, number | string>;
}

const ERRORS = 'spaces.pricingRules.errors';

/** A whole number, or `null` — the same reading `@IsInt()` would take. */
function whole(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^-?\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

/**
 * Everything the server would refuse, found before the request is made.
 *
 * The one asymmetry worth naming: the DTO refuses `durationMinutes` on any unit
 * *other* than `FIXED_DURATION`, and there is no check for it here, because the
 * form only shows that field for `FIXED_DURATION` and {@link bodyOf} sends
 * `null` for every other unit. The mistake is made impossible rather than
 * caught — which matters most on an edit, where a rule that already carries a
 * block length is switched to hourly and the server would otherwise merge the
 * old value back in and refuse the save.
 */
export function ruleProblems(draft: RuleDraft): RuleProblem[] {
  const problems: RuleProblem[] = [];

  if (!draft.name.trim()) problems.push({ field: 'name', key: `${ERRORS}.nameRequired` });

  if (!PRICE_PATTERN.test(draft.price.trim())) {
    problems.push({ field: 'price', key: `${ERRORS}.price`, params: { places: MAX_PRICE_PLACES } });
  }

  if (draft.unit === PricingUnit.FIXED_DURATION) {
    const block = whole(draft.durationMinutes);
    // `priceForDuration` defaults an absent block to 60, so a FIXED_DURATION
    // rule missing it does not fail — it quietly becomes an hourly rule at the
    // block's price, which is the whole point of the rule, lost.
    if (block === null || !draft.durationMinutes.trim()) {
      problems.push({ field: 'durationMinutes', key: `${ERRORS}.durationRequired` });
    } else if (block < RULE_BOUNDS.durationMinutes.min || block > RULE_BOUNDS.durationMinutes.max) {
      problems.push({
        field: 'durationMinutes',
        key: `${ERRORS}.durationRange`,
        params: RULE_BOUNDS.durationMinutes,
      });
    }
  }

  if (draft.minMinutes.trim()) {
    const minimum = whole(draft.minMinutes);
    if (
      minimum === null ||
      minimum < RULE_BOUNDS.minMinutes.min ||
      minimum > RULE_BOUNDS.minMinutes.max
    ) {
      problems.push({
        field: 'minMinutes',
        key: `${ERRORS}.minMinutesRange`,
        params: RULE_BOUNDS.minMinutes,
      });
    }
  }

  const start = draft.startTime.trim();
  const end = draft.endTime.trim();
  if (Boolean(start) !== Boolean(end)) {
    // Half a window is not a narrower window, it is no window at all:
    // `selectRule` skips the clock filter entirely unless both ends are set, so
    // a rule given only an opening time would quietly apply around the clock.
    problems.push({ field: 'window', key: `${ERRORS}.windowBothEnds` });
  } else if (start && end) {
    if (!CLOCK_PATTERN.test(start) || !CLOCK_PATTERN.test(end)) {
      problems.push({ field: 'window', key: `${ERRORS}.clock` });
    } else if (start >= end) {
      // A window is read inside one day — `hhmm >= start && hhmm <= end` — so
      // one that wraps past midnight matches nothing at all.
      problems.push({ field: 'window', key: `${ERRORS}.windowOrder` });
    }
  }

  if (whole(draft.priority) === null) {
    problems.push({ field: 'priority', key: `${ERRORS}.priorityInteger` });
  }

  return problems;
}

export function problemFor(problems: RuleProblem[], field: RuleField): RuleProblem | undefined {
  return problems.find((problem) => problem.field === field);
}

/**
 * A `CreateResourcePricingRuleDto` / `UpdateResourcePricingRuleDto` body.
 *
 * Every key is sent on every save, `null` where the manager cleared something,
 * and that is deliberate rather than lazy. `PATCH` validates the row it *would
 * produce* — an omitted key keeps the stored value — so turning a rule's
 * evening window off by leaving `startTime` out would leave the window exactly
 * where it was, and the screen would show a change that never happened. `null`
 * is what clears a field; `@IsOptional()` treats it as absent for validation and
 * the service writes it through.
 */
export function bodyOf(draft: RuleDraft) {
  const start = draft.startTime.trim();
  const end = draft.endTime.trim();
  const minimum = draft.minMinutes.trim();

  return {
    name: draft.name.trim(),
    unit: draft.unit,
    price: draft.price.trim(),
    // Read for FIXED_DURATION and for nothing else. A PER_HOUR rule carrying
    // 120 is refused by the API, so the other units clear it rather than
    // carrying a value the manager cannot see.
    durationMinutes:
      draft.unit === PricingUnit.FIXED_DURATION ? Number(draft.durationMinutes.trim()) : null,
    minMinutes: minimum ? Number(minimum) : null,
    // Sorted and deduplicated the way the service stores it, so a rule saved
    // twice from two different click orders is one rule, not two the overlap
    // check has to treat as different.
    daysOfWeek: [...new Set(draft.daysOfWeek)].sort((a, b) => a - b),
    startTime: start && end ? start : null,
    endTime: start && end ? end : null,
    priority: Number(draft.priority.trim()),
    isActive: draft.isActive,
  };
}

/** One line of the worked example: this much play costs this much money. */
export interface WorkedExample {
  /** Minutes of play, or `null` for a flat rate that does not depend on it. */
  minutes: number | null;
  amount: number;
}

const SAMPLE_MINUTES = [30, 60, 120];

/**
 * What the rule actually charges, for three lengths of visit.
 *
 * Priced with `priceForDuration` from `@/lib/domain` — the very function the
 * meter and the API bill with — rather than by multiplying the price out here.
 * The rounding is the part a manager cannot predict and most needs to see:
 * every unit rounds *up* to the next whole unit, so an hourly rule bills two
 * hours for a 61-minute visit, and a minimum of 30 makes a ten-minute visit
 * cost the same as a half-hour one. A screen that showed only "25.00 per hour"
 * would be hiding exactly the arithmetic that surprises people.
 */
export function workedExamples(draft: RuleDraft): WorkedExample[] {
  const price = Number(draft.price.trim());
  if (!Number.isFinite(price)) return [];

  const rule = {
    unit: draft.unit,
    price,
    durationMinutes:
      draft.unit === PricingUnit.FIXED_DURATION ? Number(draft.durationMinutes.trim()) || 60 : null,
    minMinutes: Number(draft.minMinutes.trim()) || null,
  };

  // A flat rate is the same number for every visit, so three identical rows
  // would say less than one row that admits the length is irrelevant.
  if (draft.unit === PricingUnit.FIXED_PRICE) {
    return [{ minutes: null, amount: priceForDuration(rule, 3600) }];
  }

  return SAMPLE_MINUTES.map((minutes) => ({
    minutes,
    amount: priceForDuration(rule, minutes * 60),
  }));
}

/** The same worked example for a rule already stored, so a list can show it too. */
export function workedExamplesFor(rule: PricingRuleRow): WorkedExample[] {
  return workedExamples(draftOf(rule));
}
